import { SimpleFilter } from './Filters';
import {
	AIContactReference,
	BaseIdModel,
	BaseResourceModel,
	FilterOperator,
	FilterRequestBase,
	IActor,
	IBaseApiTypeModel,
	IEmailContentGenerationRequest,
	IFileAttachment,
	IGoogleBusinessReview,
	IRawRichTextContentState,
	UserReference,
} from './index';

export enum AIConversationType {
	Default = 'Default',
	EmailContentGeneration = 'EmailContentGeneration',
	SocialMediaContentGeneration = 'SocialMediaContentGeneration',
	Translation = 'Translation',
	GoogleReviewReply = 'GoogleReviewReply',
	MediaKeywordGeneration = 'MediaKeywordGeneration',
	BlogGeneration = 'BlogGeneration',
	ContactClassification = 'ContactClassification',
	CopyMage = 'CopyMage',
	SalesLiveCoaching = 'SalesLiveCoaching',
	EmailLeadParser = 'EmailLeadParser',
	SalesPostCallCoaching = 'SalesPostCallCoaching',
	SalesPreCallCoaching = 'SalesPreCallCoaching',
	SalesDemoCallCoaching = 'SalesDemoCallCoaching',
	SiteWide = 'SiteWide',
	Admin = 'Admin',
	SalesPracticeCall = 'SalesPracticeCall',
	ImageGeneration = 'ImageGeneration',
	AccountOnboarding = 'AccountOnboarding',
	SuccessMeetingPrep = 'SuccessMeetingPrep',
}

export enum AIChatRole {
	Assistant = 'Assistant',
	System = 'System',
	User = 'User',
}

export interface AIConversation extends BaseResourceModel {
	participants: AIConversationParticipant[];
	conversationType?: AIConversationType;
}

interface AIConversationParticipant {
	id: string;
}

export interface AIUserParticipant extends AIConversationParticipant {
	userId?: string;
	reference?: UserReference;
}

export interface AIAssistantParticipant extends AIConversationParticipant {
	assistantName?: string;
}

type AIChatContent = IBaseApiTypeModel;

export interface AITextContent extends AIChatContent {
	_type: 'AITextContent';
	text: string;
}

export interface AIToolContent extends AIChatContent {
	_type: 'AIToolContent';
	toolCall: AIToolCall;
}

export interface AIToolCall extends IBaseApiTypeModel {
	id: string;
	toolName: string;
	propertyName: string;
	value: string;
	toolOutputJson?: string;
	toolOutputType?: string;
}

export interface EmailToolCall extends AIToolCall {
	subject: string;
	body: IRawRichTextContentState;
	signature?: IRawRichTextContentState;
}

export interface ContactToolCall extends AIToolCall {
	contactId: string;
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	company: string;
}

export interface SocialMediaToolCall extends AIToolCall {
	content: IRawRichTextContentState;
}

export interface GoogleReviewReplyToolCall extends AIToolCall {
	reply: string;
}

export interface BlogToolCall extends AIToolCall {
	title: string;
	content: IRawRichTextContentState;
}

export interface ContactListToolCall extends AIToolCall {
	contacts: AIContactReference[];
	filters: SimpleFilter[];
	totalCount: number;
}

export interface NavigationMetadata {
	id: string;
	entityType: string;
	parentNavigationMetadata?: NavigationMetadata;
}

export interface NavigationToolCall extends AIToolCall {
	navigationItems: NavigationMetadata[];
	totalCount: number;
}

export interface MediaContentToolCall extends AIToolCall {
	systemJobId?: string;
	fileAttachments?: IFileAttachment[];
}

export interface AIChatMessage extends BaseResourceModel {
	participantId?: string;
	aiConversationId?: string;
	role?: AIChatRole;
	contents?: AIChatContent[];
	text?: string;
	fileAttachments?: IFileAttachment[];
}

export type AIContext = IBaseApiTypeModel;
export interface EmailAIContext extends AIContext {
	_type: 'EmailAIContext';
	subject: string;
	body: IRawRichTextContentState;
}

export interface SocialMediaAIContext extends AIContext {
	_type: 'SocialMediaAIContext';
	content: IRawRichTextContentState;
}

export interface GoogleReviewAIContext extends AIContext {
	type: 'GoogleReviewAIContext';
	review: IGoogleBusinessReview;
	reviewedCompanyName: string;
	reply: string;
}

export interface BlogAIContext extends AIContext {
	type: 'BlogAIContext';
	title: string;
	content: IRawRichTextContentState;
}

export interface SalesLeadAIContext extends AIContext {
	_type: 'SalesLeadAIContext';
	companyId: string;
}

export interface MediaAIContext extends AIContext {
	_type: 'MediaAIContext';
	fileAttachmentIds?: string[];
	urls?: string[];
	base64Images?: string[];
}

export interface AIChatRequest extends IBaseApiTypeModel {
	contexts?: AIContext[];
}

export interface AIMessageRequest extends AIChatRequest {
	chatMessages?: AIChatMessage[];
}

export interface AIActionRequest extends AIChatRequest {
	action?: AIAction;
}

export interface AIAction extends BaseIdModel {
	name?: string;
	description?: string;
}

export interface EmailContentGenerationAction extends AIAction {
	contentGenerationRequest?: IEmailContentGenerationRequest;
}

export interface GoogleReviewReplyAction extends AIAction {
	reviewId?: string;
}

export enum ContentConversionType {
	Email = 'Email',
	Blog = 'Blog',
	Social = 'Social',
}

export interface ContentConversionAction extends AIAction {
	_type: 'ContentConversionAction';
	fromContentType: ContentConversionType;
	toContentType: ContentConversionType;
	contentId?: string;
	content?: IRawRichTextContentState;
}

export enum AIConversationFilterProperty {
	ConversationType,
	CreatorId,
}

export interface AIConversationFilterCriteria {
	criteria: AIConversationFilterCriteria[];
	op?: FilterOperator;
	property: AIConversationFilterProperty;
	value?: string;
	values?: Record<string, string>;
}

export type AIConversationFilter = FilterRequestBase<AIConversationFilterProperty, AIConversationFilterProperty>;

export interface AIConversationStarter {
	title: string;
	subTitle?: string;
	chatMessages: AIChatMessage[];
}

export interface AIChatStreamResponse {
	id: string;
	isDelta: boolean;
}

interface AIMessage {
	text: string;
}
export interface AIChatStreamMessageResponse extends AIChatStreamResponse {
	message: AIMessage;
}

export interface AIChatStreamToolResponse extends AIChatStreamResponse {
	toolCall: AIToolCall;
}

// AI Agent Management Types
export interface IAIAgentTemplate extends BaseResourceModel {
	name: string;
	description?: string;
	isActive: boolean;
	objectivePrompt?: string;
	technicalPrompt?: string;
	vectorStore?: IAIVectorStore;
	model?: string;
	attachments?: IAIAgentTemplateAttachment[];
	conversationType?: AIConversationType;
	selectors?: Record<string, string>;
	actor?: IActor;
}

export interface IAIAgentTemplateAttachment {
	fileAttachment: IFileAttachment;
	vectorStoreFileId?: string;
}

export interface IAIVectorStore {
	id?: string;
	_type: string;
}

export interface IOpenAIVectorStore extends IAIVectorStore {
	_type: 'OpenAIVectorStore';
	storeId?: string;
}

// Form data interface for editing
export interface IAIAgentTemplateFormData {
	name: string;
	description: string;
	objectivePrompt: string;
	technicalPrompt: string;
	model: string;
	conversationType: string;
	selectors: Record<string, string>;
	attachments: IAIAgentTemplateAttachment[];
}

// Group interface for organizing templates by selector hash
export interface IAIAgentTemplateGroup {
	hash: string;
	templates: IAIAgentTemplate[];
	selectors: Record<string, string>;
}
